import React, { useState, useEffect, useMemo } from 'react';
import { useNavigation } from '../navigation';
import { useTabAnimation } from '../hooks/useTabAnimation';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Utility function to get auth token
const getAuthToken = () => {
  const token = localStorage.getItem('authToken');
  console.log('=== Auth Token Debug ===');
  console.log('Token exists:', !!token);
  console.log('Token length:', token ? token.length : 0);
  console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
  return token;
};


interface BillItem {
  id: number;
  name: string;
  medicine_composition: string;
  medicine_company: string;
  batch_no: string;
  quantity: string | number;
  quantity_type: string;
  unit_cost: string | number;
  mrp: string | number;
  gst: string | number;
  expiry_date: string;
  discount: number;
  storage_location: string;
  tablets_per_strip: number;
  total_tablets: number;
  selling_price: string | number;
}


interface Customer {
  id: number;
  name: string;
  phone: string;
  email: string;
  address: string;
}

interface CheckoutBillingProps {
  showHeader?: boolean;
}

// Column helper for TanStack Table
const columnHelper = createColumnHelper<BillItem>();


const CheckoutBilling: React.FC<CheckoutBillingProps> = ({ showHeader = true }) => {
  const { navigateTo } = useNavigation();
  const { activeTab, isAnimating, getTabPosition, getButtonColor, triggerAnimation } = useTabAnimation();
  
  // Debug animation state changes
  React.useEffect(() => {
    // Animation state changes - logging removed
  }, [activeTab, isAnimating, getTabPosition]);

  // Medicine search states - per row
  const [medicineSearchResults, setMedicineSearchResults] = useState<{ [key: number]: any[] }>({});
  const [isSearching, setSearching] = useState<{ [key: number]: boolean }>({});
  const [searchDropdownOpen, setSearchDropdownOpen] = useState<{ [key: number]: boolean }>({});
  const [searchQuery, setSearchQuery] = useState<{ [key: number]: string }>({});
  
  // Portal dropdown state
  const [activeDropdown, setActiveDropdown] = useState<{
    rowId: number;
    results: any[];
    isLoading: boolean;
  } | null>(null);

  // Keyboard navigation state
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | null>(null);

  // Function to scroll selected item into view
  const scrollToSelectedItem = (index: number) => {
    if (dropdownRef && index >= 0) {
      const selectedElement = dropdownRef.querySelector(`[data-medicine-index="${index}"]`) as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  };

  // Handle scroll and resize events
  useEffect(() => {
    if (activeDropdown) {
      const handleScroll = () => {
        // Force re-render to update position
        setActiveDropdown(prev => prev ? { ...prev } : null);
      };
      const handleResize = () => {
        // Force re-render to update position
        setActiveDropdown(prev => prev ? { ...prev } : null);
      };
      
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [activeDropdown]);

  // Handle keyboard navigation
  useEffect(() => {
    if (activeDropdown && activeDropdown.results.length > 0) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const newIndex = selectedIndex < activeDropdown.results.length - 1 ? selectedIndex + 1 : 0;
          setSelectedIndex(newIndex);
          scrollToSelectedItem(newIndex);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const newIndex = selectedIndex > 0 ? selectedIndex - 1 : activeDropdown.results.length - 1;
          setSelectedIndex(newIndex);
          scrollToSelectedItem(newIndex);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < activeDropdown.results.length) {
            handleMedicineSelect(activeDropdown.rowId, activeDropdown.results[selectedIndex]);
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setActiveDropdown(null);
          setSelectedIndex(-1);
        }
      };
      
      window.addEventListener('keydown', handleKeyDown);
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [activeDropdown, selectedIndex]);

  // API function to search medicines for a specific row
  const searchMedicines = async (query: string, rowId: number) => {
    console.log('🔍 ===== MEDICINE SEARCH STARTED =====');
    console.log('📍 Row ID:', rowId);
    console.log('🔤 Search Query:', query);
    console.log('⏰ Timestamp:', new Date().toLocaleTimeString());
    
    if (!query.trim()) {
      console.log('❌ Empty query, clearing results');
      setMedicineSearchResults(prev => ({ ...prev, [rowId]: [] }));
      return;
    }

    try {
      console.log('⏳ Setting loading state for row:', rowId);
      setSearching(prev => ({ ...prev, [rowId]: true }));
      
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/search?q=${encodeURIComponent(query)}&limit=10`;
      console.log('🌐 API URL:', apiUrl);
      
      const authToken = getAuthToken();
      console.log('🔑 Auth Token:', authToken ? 'Present' : 'Missing');
      
      console.log('📡 Making API request...');
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📊 Response Status:', response.status);
      console.log('📊 Response OK:', response.ok);
      console.log('📊 Response Headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const data = await response.json();
        console.log('✅ API Response Success!');
        console.log('📦 Raw Response Data:', data);
        
        // Extract the inventory array from the response
        const inventoryData = data.inventory || [];
        console.log('📊 Inventory Array:', inventoryData);
        console.log('📊 Results Count:', inventoryData.length);
        
        if (inventoryData.length > 0) {
          console.log('💊 First Medicine Sample:', inventoryData[0]);
        }
        
        setMedicineSearchResults(prev => ({ ...prev, [rowId]: inventoryData }));
        console.log('💾 Results saved to state for row:', rowId);
        
        // Update active dropdown if it's for this row
        setActiveDropdown(prev => {
          if (prev && prev.rowId === rowId) {
            return {
              ...prev,
              results: inventoryData,
              isLoading: false
            };
          }
          return prev;
        });
        setSelectedIndex(-1); // Reset keyboard selection when new results arrive
      } else {
        console.error('❌ API Request Failed!');
        console.error('📊 Status:', response.status);
        console.error('📊 Status Text:', response.statusText);
        const errorText = await response.text();
        console.error('📊 Error Response:', errorText);
        setMedicineSearchResults(prev => ({ ...prev, [rowId]: [] }));
      }
    } catch (error) {
      console.error('💥 API Request Error!');
      console.error('📊 Error Type:', error.constructor.name);
      console.error('📊 Error Message:', error.message);
      console.error('📊 Full Error:', error);
      setMedicineSearchResults(prev => ({ ...prev, [rowId]: [] }));
    } finally {
      console.log('🏁 Setting loading state to false for row:', rowId);
      setSearching(prev => ({ ...prev, [rowId]: false }));
      console.log('🔍 ===== MEDICINE SEARCH COMPLETED =====');
    }
  };

  // Debounced search function
  const debouncedSearch = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (query: string, rowId: number) => {
      console.log('⏱️ Debounced search triggered for row:', rowId, 'query:', query);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('🚀 Executing debounced search after 300ms delay');
        searchMedicines(query, rowId);
      }, 300); // 300ms delay
    };
  }, []);

  // Function to create a new empty item
  const createEmptyItem = (id: number): BillItem => ({
    id,
      name: '',
      medicine_composition: '',
      medicine_company: '',
      batch_no: '',
      quantity: '',
      quantity_type: 'individual',
      unit_cost: '',
      mrp: '',
      gst: '',
      expiry_date: '',
      discount: 0,
      storage_location: '',
      tablets_per_strip: 1,
      total_tablets: 0,
      selling_price: ''
  });

  // Function to ensure there are always empty rows available (simplified)
  const ensureEmptyRows = (items: BillItem[]): BillItem[] => {
    const emptyItems = items.filter(item => 
      item.name.trim() === '' && 
      item.medicine_composition.trim() === '' && 
      item.medicine_company.trim() === '' &&
      item.quantity === '' &&
      item.selling_price === ''
    );
    
    // Only add rows if we have less than 1 empty row
    if (emptyItems.length < 1) {
      const maxId = Math.max(...items.map(item => item.id), 0);
      const newEmptyItem = createEmptyItem(maxId + 1);
      return [...items, newEmptyItem];
    }
    
    return items;
  };

  const [billItems, setBillItems] = useState<BillItem[]>([
    createEmptyItem(1),
    createEmptyItem(2)
  ]);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  const [customers, setCustomers] = useState<Customer[]>([
    { id: 1, name: 'Rajesh Kumar', phone: '+91 98765 43210', email: 'rajesh@email.com', address: '123 Main St, Mumbai' },
    { id: 2, name: 'Priya Sharma', phone: '+91 87654 32109', email: 'priya@email.com', address: '456 Park Ave, Delhi' }
  ]);

  // Generic keyboard navigation hook for dropdowns
  const useKeyboardNavigation = (items: any[], isOpen: boolean, onSelect: (item: any) => void, onClose: () => void) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isOpen || items.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => prev < items.length - 1 ? prev + 1 : prev);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
          break;
        case 'Enter':
          e.preventDefault();
          if (items[selectedIndex]) {
            onSelect(items[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    const resetSelection = () => setSelectedIndex(0);

    return {
      selectedIndex,
      handleKeyDown,
      resetSelection
    };
  };


  // Reusable dropdown component with keyboard navigation
  const DropdownWithKeyboardNav = ({ 
    items, 
    isOpen, 
    onSelect, 
    onClose, 
    renderItem
  }: {
    items: any[];
    isOpen: boolean;
    onSelect: (item: any) => void;
    onClose: () => void;
    renderItem: (item: any, index: number, isSelected: boolean) => React.ReactNode;
  }) => {
    const keyboardNav = useKeyboardNavigation(items, isOpen, onSelect, onClose);
    
    if (!isOpen || items.length === 0) return null;

    return (
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        margin: '4px 0'
      }}>
        <div style={{
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: 'white',
          fontWeight: '600',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px'
          }}>
            💊
          </div>
          <div style={{ flex: 1 }}>
            Select Medicine ({items.length} results)
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '4px',
              padding: '2px 6px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            ✕
          </button>
        </div>
        <div style={{
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {items.map((item, index) => renderItem(item, index, index === keyboardNav.selectedIndex))}
        </div>
      </div>
    );
  };


  const [showBillPreview, setShowBillPreview] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [focusedMedicineRow, setFocusedMedicineRow] = useState<number | null>(null);
  const [headerScrollRef, setHeaderScrollRef] = useState<HTMLDivElement | null>(null);
  const [bodyScrollRef, setBodyScrollRef] = useState<HTMLDivElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef && bodyScrollRef) {
      headerScrollRef.scrollLeft = e.currentTarget.scrollLeft;
    }
    setScrollPosition(e.currentTarget.scrollLeft);
    setScrollWidth(e.currentTarget.scrollWidth);
    setClientWidth(e.currentTarget.clientWidth);
  };

  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (bodyScrollRef && headerScrollRef) {
      bodyScrollRef.scrollLeft = e.currentTarget.scrollLeft;
    }
    setScrollPosition(e.currentTarget.scrollLeft);
    setScrollWidth(e.currentTarget.scrollWidth);
    setClientWidth(e.currentTarget.clientWidth);
  };

  // Calculate scrollbar indicator position and width
  const getScrollbarStyle = () => {
    if (scrollWidth <= clientWidth) {
      return { display: 'none' as const };
    }
    
    const scrollbarWidth = (clientWidth / scrollWidth) * clientWidth;
    const scrollbarPosition = (scrollPosition / (scrollWidth - clientWidth)) * (clientWidth - scrollbarWidth);
    
    return {
      width: `${scrollbarWidth}px`,
      left: `${scrollbarPosition}px`,
      height: '4px',
      background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
      borderRadius: '2px',
      position: 'absolute' as const,
      bottom: '2px',
      zIndex: 2,
      transition: 'all 0.1s ease',
      pointerEvents: 'none' as const
    };
  };

  // Initialize scroll dimensions
  useEffect(() => {
    if (bodyScrollRef) {
      setScrollWidth(bodyScrollRef.scrollWidth);
      setClientWidth(bodyScrollRef.clientWidth);
    }
  }, [bodyScrollRef, billItems]);
  
  // Sales form state
  const [salesFormFocused, setSalesFormFocused] = useState(false);
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const [salesForm, setSalesForm] = useState({
    customerName: '',
    customerPhone: '',
    paymentMode: 'cash',
    amountPaid: ''
  });
  const [isSubmittingSale, setIsSubmittingSale] = useState(false);
  const [saleSubmissionError, setSaleSubmissionError] = useState<string | null>(null);
  const [showBillConfirmation, setShowBillConfirmation] = useState(false);
  const [generatedBillData, setGeneratedBillData] = useState<any>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
      
      // Handle payment mode dropdown
      if (!target.closest('[data-payment-dropdown]')) {
        setIsPaymentDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debug logging for response message
  React.useEffect(() => {
    if (generatedBillData) {
      console.log('📋 Generated Bill Data:', generatedBillData);
      console.log('📋 Message fields:', {
        message: generatedBillData?.message,
        response_message: generatedBillData?.response_message,
        note: generatedBillData?.note
      });
    }
  }, [generatedBillData]);

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculateSubtotal = () => {
    return billItems.reduce((sum, item) => {
      const sellingPrice = typeof item.selling_price === 'string' ? parseFloat(item.selling_price) || 0 : item.selling_price;
      const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity;
      return sum + (sellingPrice * quantity);
    }, 0);
  };

  const calculateGST = () => {
    return billItems.reduce((sum, item) => {
      const sellingPrice = typeof item.selling_price === 'string' ? parseFloat(item.selling_price) || 0 : item.selling_price;
      const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity;
      const gstRate = typeof item.gst === 'string' ? parseFloat(item.gst) || 0 : item.gst;
      const discount = typeof item.discount === 'string' ? parseFloat(item.discount) || 0 : item.discount;
      
      // Calculate subtotal, apply discount, then calculate GST
      const itemSubtotal = sellingPrice * quantity;
      const discountAmount = itemSubtotal * (discount / 100);
      const afterDiscount = itemSubtotal - discountAmount;
      const gstAmount = afterDiscount * (gstRate / 100);
      
      return sum + gstAmount;
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateGST();
  };

  const calculateDiscount = () => {
    return billItems.reduce((sum, item) => {
      const sellingPrice = typeof item.selling_price === 'string' ? parseFloat(item.selling_price) || 0 : item.selling_price;
      const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity;
      const discount = typeof item.discount === 'string' ? parseFloat(item.discount) || 0 : item.discount;
      
      const itemSubtotal = sellingPrice * quantity;
      const discountAmount = itemSubtotal * (discount / 100);
      
      return sum + discountAmount;
    }, 0);
  };

  const calculateFinalTotal = () => {
    return calculateTotal() - calculateDiscount();
  };

  const handleQuantityChange = (id: number, newQuantity: string | number) => {
    setBillItems(items => items.map(item => 
      item.id === id ? { ...item, quantity: newQuantity } : item
    ));
  };

  const handleQuantityTypeChange = (id: number, newType: string) => {
    setBillItems(items => items.map(item => 
      item.id === id ? { ...item, quantity_type: newType } : item
    ));
  };

  const handleItemFieldChange = (id: number, field: keyof BillItem, value: string | number) => {
    setBillItems(items => {
      const updatedItems = items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
      );
      return ensureEmptyRows(updatedItems);
    });
  };

  // Special handler for medicine name that includes search functionality
  const handleMedicineNameChange = (id: number, value: string, inputElement?: HTMLInputElement) => {
    console.log('📝 ===== MEDICINE NAME CHANGE =====');
    console.log('📍 Row ID:', id);
    console.log('🔤 New Value:', value);
    console.log('📏 Value Length:', value.length);
    console.log('🔍 Current activeDropdown:', activeDropdown);
    
    setBillItems(items => items.map(item => 
      item.id === id ? { ...item, name: value } : item
    ));
    
    // Update search query and trigger search
    setSearchQuery(prev => ({ ...prev, [id]: value }));
    setSearchDropdownOpen(prev => ({ ...prev, [id]: value.length > 0 }));
    
    console.log('🔍 Medicine search triggered:', { id, value, dropdownOpen: value.length > 0 });
    
    if (value.length > 0) {
      console.log('✅ Value has content, triggering debounced search');
      debouncedSearch(value, id);
      
      // Set dropdown state
      console.log('🎯 Setting activeDropdown for row:', id);
      setActiveDropdown({
        rowId: id,
        results: medicineSearchResults[id] || [],
        isLoading: isSearching[id] || false
      });
      setSelectedIndex(-1); // Reset keyboard selection
      
      console.log('📍 Dropdown activated for row:', id);
      console.log('📍 New activeDropdown state:', {
        rowId: id,
        results: medicineSearchResults[id] || [],
        isLoading: isSearching[id] || false
      });
    } else {
      console.log('❌ Empty value, clearing search results');
      setMedicineSearchResults(prev => ({ ...prev, [id]: [] }));
      setActiveDropdown(null);
    }
    console.log('📝 ===== MEDICINE NAME CHANGE COMPLETED =====');
  };

  // Handle medicine selection from dropdown
  const handleMedicineSelect = (id: number, medicine: any) => {
    setBillItems(items => items.map(item => 
      item.id === id ? {
        ...item,
        name: medicine.name,
        medicine_composition: medicine.medicine_composition,
        medicine_company: medicine.medicine_company,
        batch_no: medicine.batch_no,
        unit_cost: medicine.unit_cost,
        mrp: medicine.mrp,
        quantity: medicine.quantity,
        expiry_date: medicine.expiry_date,
        storage_location: medicine.storage_location,
        selling_price: medicine.mrp // Default selling price to MRP
      } : item
    ));
    
    // Close dropdown and clear search for this specific row
    setSearchDropdownOpen(prev => ({ ...prev, [id]: false }));
    setSearchQuery(prev => ({ ...prev, [id]: medicine.name }));
    setMedicineSearchResults(prev => ({ ...prev, [id]: [] }));
    setActiveDropdown(null);
    setSelectedIndex(-1); // Reset keyboard selection
  };



  const removeItem = (id: number) => {
    setBillItems(items => {
      const filteredItems = items.filter(item => item.id !== id);
      return ensureEmptyRows(filteredItems);
    });
  };

  const addNewItem = () => {
    const newId = Math.max(...billItems.map(item => item.id), 0) + 1;
    setBillItems(items => ensureEmptyRows([...items, createEmptyItem(newId)]));
  };

  // Define table columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'S.No',
        cell: ({ row }) => row.index + 1,
        size: 60,
      }),
      columnHelper.accessor('name', {
        header: 'Medicine Name',
        cell: ({ getValue, row }) => (
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              data-row-id={row.original.id}
              value={row.original.name || ''}
              onChange={(e) => {
                const value = e.target.value;
                handleMedicineNameChange(row.original.id, value, e.target);
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
                // Delay closing dropdown to allow for clicks
                setTimeout(() => {
                  setActiveDropdown(null);
                }, 200);
              }}
              placeholder="Enter medicine name"
              className="table-input"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.875rem',
                outline: 'none',
                backgroundColor: '#ffffff',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                minHeight: '44px'
              }}
            />
            
          </div>
        ),
        size: 180,
      }),
      columnHelper.accessor('medicine_composition', {
        header: 'Composition',
        cell: ({ getValue, row }) => (
          <input
            type="text"
            data-field="composition"
            value={row.original.medicine_composition || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'medicine_composition', e.target.value);
            }}
            placeholder="Composition"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 150,
      }),
      columnHelper.accessor('medicine_company', {
        header: 'Company',
        cell: ({ getValue, row }) => (
          <input
            type="text"
            data-field="company"
            value={row.original.medicine_company || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'medicine_company', e.target.value);
            }}
            placeholder="Company"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 120,
      }),
      columnHelper.accessor('batch_no', {
        header: 'Batch No',
        cell: ({ getValue, row }) => (
          <input
            type="text"
            data-field="batch_no"
            value={row.original.batch_no || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'batch_no', e.target.value);
            }}
            placeholder="Batch No"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 100,
      }),
      columnHelper.accessor('quantity_type', {
        header: 'Qty Type',
        cell: ({ getValue, row }) => {
          const quantityTypes = [
            { value: 'individual', label: 'Individual' },
            { value: 'strip', label: 'Strip' },
            { value: 'bottle', label: 'Bottle' },
            { value: 'box', label: 'Box' }
          ];

          return (
            <select
              value={getValue() || 'individual'}
              onChange={(e) => {
                e.stopPropagation();
                handleItemFieldChange(row.original.id, 'quantity_type', e.target.value);
              }}
              onKeyDown={(e) => {
                // Native select already handles arrow keys, but we can add custom behavior
                if (e.key === 'Escape') {
                  e.preventDefault();
                  // Focus next field or close dropdown
                  const nextInput = e.currentTarget.parentElement?.parentElement?.querySelector('input');
                  if (nextInput) {
                    nextInput.focus();
                  }
                }
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            >
              {quantityTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          );
        },
        size: 100,
      }),
      columnHelper.accessor('quantity', {
        header: 'Quantity',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="quantity"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'quantity', e.target.value);
            }}
            placeholder="Qty"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 80,
      }),
      columnHelper.accessor('unit_cost', {
        header: 'Unit Cost',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="unit_cost"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'unit_cost', e.target.value);
            }}
            placeholder="Unit Cost"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 90,
      }),
      columnHelper.accessor('mrp', {
        header: 'MRP',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="mrp"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'mrp', e.target.value);
            }}
            placeholder="MRP"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 80,
      }),
      columnHelper.accessor('gst', {
        header: 'GST%',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="gst"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'gst', e.target.value);
            }}
            placeholder="GST%"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 70,
      }),
      columnHelper.accessor('discount', {
        header: 'Discount',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="discount"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'discount', e.target.value);
            }}
            placeholder="Discount"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 80,
      }),
      columnHelper.accessor('storage_location', {
        header: 'Storage',
        cell: ({ getValue, row }) => (
          <input
            type="text"
            data-field="storage_location"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'storage_location', e.target.value);
            }}
            placeholder="Storage"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 100,
      }),
      columnHelper.accessor('expiry_date', {
        header: 'Expiry Date',
        cell: ({ getValue, row }) => (
          <input
            type="date"
            data-field="expiry_date"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'expiry_date', e.target.value);
            }}
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 120,
      }),
      columnHelper.accessor('tablets_per_strip', {
        header: 'Tablets/Strip',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="tablets_per_strip"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'tablets_per_strip', e.target.value);
            }}
            placeholder="Tablets"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 100,
      }),
      columnHelper.accessor('total_tablets', {
        header: 'Total Tablets',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="total_tablets"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'total_tablets', e.target.value);
            }}
            placeholder="Total"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 100,
      }),
      columnHelper.accessor('selling_price', {
        header: 'Selling Price',
        cell: ({ getValue, row }) => (
          <input
            type="number"
            data-field="selling_price"
            value={getValue() || ''}
            onChange={(e) => {
              e.stopPropagation();
              handleItemFieldChange(row.original.id, 'selling_price', e.target.value);
            }}
            placeholder="Selling Price"
            className="table-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none',
              backgroundColor: '#ffffff',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              minHeight: '44px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        ),
        size: 100,
      }),
      columnHelper.display({
        id: 'total',
        header: 'Total',
        cell: ({ row }) => {
          const sellingPrice = typeof row.original.selling_price === 'string' ? parseFloat(row.original.selling_price) || 0 : row.original.selling_price;
          const quantity = typeof row.original.quantity === 'string' ? parseFloat(row.original.quantity) || 0 : row.original.quantity;
          const discount = typeof row.original.discount === 'string' ? parseFloat(row.original.discount) || 0 : row.original.discount;
          const gstRate = typeof row.original.gst === 'string' ? parseFloat(row.original.gst) || 0 : row.original.gst;
          
          // Calculate total: (selling_price * quantity) - discount + GST
          const subtotal = sellingPrice * quantity;
          const discountAmount = subtotal * (discount / 100);
          const afterDiscount = subtotal - discountAmount;
          const gstAmount = afterDiscount * (gstRate / 100);
          const total = afterDiscount + gstAmount;
          
          return (
            <div style={{
              padding: '0.75rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#1e40af',
              textAlign: 'center',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bfdbfe'
            }}>
              ₹{total.toFixed(2)}
            </div>
          );
        },
        size: 100,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <button
            onClick={() => removeItem(row.original.id)}
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
              minWidth: '80px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
            }}
          >
            Remove
          </button>
        ),
        size: 80,
      }),
    ],
    [] // Columns don't need to change when billItems change
  );

  // Create table instance
  const table = useReactTable({
    data: billItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Debug: Log column count
  console.log('Total columns:', columns.length);
  console.log('Column headers:', columns.map(col => col.header || col.id));

  const handleAddCustomer = () => {
    if (newCustomer.name && newCustomer.phone) {
      const newId = Math.max(...customers.map(c => c.id), 0) + 1;
      const customer: Customer = {
        id: newId,
        ...newCustomer
      };
      setCustomers(prevCustomers => [...prevCustomers, customer]);
      setSelectedCustomer(customer);
      setNewCustomer({ name: '', phone: '', email: '', address: '' });
      setShowAddCustomer(false);
    } else {
      alert('Please fill in at least name and phone number');
    }
  };

  const generateBill = () => {
    // Validate required fields from Sales Information form
    if (!salesForm.customerName.trim()) {
      alert('Please enter customer name in Sales Information');
      return;
    }
    if (!salesForm.customerPhone.trim()) {
      alert('Please enter customer phone in Sales Information');
      return;
    }
    
    // Check if there are any valid items
    const validItems = billItems.filter(item => 
      item.name.trim() !== '' && 
      item.quantity !== '' && 
      item.selling_price !== ''
    );
    
    if (validItems.length === 0) {
      alert('Please add at least one item to the bill');
      return;
    }
    
    // Close payment dropdown and show bill preview
    setIsPaymentDropdownOpen(false);
    
    // Force hide payment dropdown elements
    setTimeout(() => {
      const paymentDropdowns = document.querySelectorAll('[data-payment-dropdown]');
      paymentDropdowns.forEach(dropdown => {
        if (dropdown instanceof HTMLElement) {
          dropdown.style.display = 'none';
          dropdown.style.zIndex = '1';
        }
      });
    }, 100);
    
    setShowBillPreview(true);
  };

  const closeBillPreview = () => {
    setShowBillPreview(false);
    
    // Restore payment dropdown elements
    setTimeout(() => {
      const paymentDropdowns = document.querySelectorAll('[data-payment-dropdown]');
      paymentDropdowns.forEach(dropdown => {
        if (dropdown instanceof HTMLElement) {
          dropdown.style.display = '';
          dropdown.style.zIndex = '';
        }
      });
    }, 100);
  };

  const downloadBill = () => {
    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to download the bill');
      return;
    }

    const billDate = new Date().toLocaleDateString('en-IN');
    const billTime = new Date().toLocaleTimeString('en-IN');
    const billNumber = `BILL-${Date.now()}`;
    
    const subtotal = calculateSubtotal();
    const gst = calculateGST();
    const discount = calculateDiscount();
    const total = calculateFinalTotal();

    const billHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pharmacy Bill - ${billNumber}</title>
        <style>
          body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            color: #333;
          }
          .bill-header {
            text-align: center;
            border-bottom: 3px solid #1B5E20;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .pharmacy-name {
            font-size: 28px;
            font-weight: bold;
            color: #1B5E20;
            margin-bottom: 5px;
          }
          .pharmacy-details {
            font-size: 14px;
            color: #666;
            line-height: 1.4;
          }
          .bill-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            flex-wrap: wrap;
          }
          .bill-details, .customer-details {
            flex: 1;
            min-width: 250px;
          }
          .bill-details h3, .customer-details h3 {
            color: #1B5E20;
            font-size: 16px;
            margin-bottom: 10px;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 5px;
          }
          .bill-details p, .customer-details p {
            margin: 5px 0;
            font-size: 14px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          .items-table th {
            background: #1B5E20;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-size: 14px;
            font-weight: bold;
          }
          .items-table td {
            padding: 10px 8px;
            border-bottom: 1px solid #e0e0e0;
            font-size: 13px;
          }
          .items-table tr:nth-child(even) {
            background: #f8f9fa;
          }
          .summary-section {
            margin-top: 20px;
            border-top: 2px solid #1B5E20;
            padding-top: 20px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            margin: 8px 0;
            font-size: 14px;
          }
          .summary-label {
            font-weight: 500;
          }
          .summary-value {
            font-weight: 600;
          }
          .total-row {
            border-top: 2px solid #1B5E20;
            padding-top: 10px;
            margin-top: 15px;
            font-size: 18px;
            font-weight: bold;
            color: #1B5E20;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #e0e0e0;
            padding-top: 20px;
          }
          @media print {
            body { margin: 0; }
            .bill-header { page-break-inside: avoid; }
            .items-table { page-break-inside: avoid; }
            .summary-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="bill-header">
          <div class="pharmacy-name">GREEN VALLEY PHARMACY</div>
          <div class="pharmacy-details">
            123 Health Street, Medical District<br>
            Mumbai, Maharashtra - 400001<br>
            Phone: +91 98765 43210 | Email: info@greenvalleypharmacy.com<br>
            GST No: 27ABCDE1234F1Z5 | License: MH/2023/PH/001
          </div>
        </div>

        <div class="bill-info">
          <div class="bill-details">
            <h3>Bill Information</h3>
            <p><strong>Bill No:</strong> ${billNumber}</p>
            <p><strong>Date:</strong> ${billDate}</p>
            <p><strong>Time:</strong> ${billTime}</p>
            <p><strong>Cashier:</strong> Admin</p>
          </div>
          <div class="customer-details">
            <h3>Customer Information</h3>
            <p><strong>Name:</strong> ${generatedBillData?.customerName || salesForm.customerName || 'N/A'}</p>
            <p><strong>Phone:</strong> ${generatedBillData?.customerPhone || salesForm.customerPhone || 'N/A'}</p>
            <p><strong>Email:</strong> N/A</p>
            <p><strong>Address:</strong> N/A</p>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 5%;">S.No</th>
              <th style="width: 35%;">Medicine Name</th>
              <th style="width: 15%;">Brand</th>
              <th style="width: 10%;">Qty</th>
              <th style="width: 10%;">Price</th>
              <th style="width: 10%;">GST%</th>
              <th style="width: 15%;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${billItems.filter(item => 
              item.name.trim() !== '' && 
              item.quantity !== '' && 
              item.selling_price !== ''
            ).map((item, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${item.name}</td>
                <td>${item.medicine_company}</td>
                <td>${item.quantity}</td>
                <td>₹${(typeof item.selling_price === 'string' ? parseFloat(item.selling_price) || 0 : item.selling_price).toFixed(2)}</td>
                <td>${typeof item.gst === 'string' ? parseFloat(item.gst) || 0 : item.gst}%</td>
                <td>₹${((typeof item.selling_price === 'string' ? parseFloat(item.selling_price) || 0 : item.selling_price) * (typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity)).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="summary-section">
          <div class="summary-row">
            <span class="summary-label">Subtotal:</span>
            <span class="summary-value">₹${subtotal.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">GST (${billItems.reduce((sum, item) => sum + (typeof item.gst === 'string' ? parseFloat(item.gst) || 0 : item.gst), 0) / billItems.length}%):</span>
            <span class="summary-value">₹${gst.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Total:</span>
            <span class="summary-value">₹${(subtotal + gst).toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Discount:</span>
            <span class="summary-value">-₹${discount.toFixed(2)}</span>
          </div>
          <div class="summary-row total-row">
            <span class="summary-label">Final Total:</span>
            <span class="summary-value">₹${total.toFixed(2)}</span>
          </div>
        </div>

        <div class="footer">
          <p><strong>Thank you for choosing Green Valley Pharmacy!</strong></p>
          <p>For any queries, please contact us at +91 98765 43210</p>
          <p>This is a computer-generated bill and does not require signature</p>
          <p>Generated on ${billDate} at ${billTime}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(billHTML);
    printWindow.document.close();
    
    // Wait for content to load, then trigger download
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Close the window after a short delay
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      }, 500);
    };

    alert('Bill downloaded successfully! PDF will open for printing.');
  };

  const handleSalesFormChange = (field: string, value: string | number) => {
    setSalesForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitSale = async () => {
    try {
      setIsSubmittingSale(true);
      setSaleSubmissionError(null);
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Validate required fields
      if (!salesForm.customerName.trim()) {
        throw new Error('Customer name is required');
      }
      if (!salesForm.customerPhone.trim()) {
        throw new Error('Customer phone is required');
      }
      if (!salesForm.amountPaid.trim()) {
        throw new Error('Amount paid is required');
      }

      // Filter out empty items
      const validItems = billItems.filter(item => 
        item.name.trim() !== '' && 
        item.quantity !== '' && 
        item.mrp !== ''
      );

      if (validItems.length === 0) {
        throw new Error('Please add at least one medicine item');
      }

      // Prepare items for API according to new format
      const items = validItems.map(item => {
        const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity;
        const quantityType = item.quantity_type;
        const sellingPrice = typeof item.selling_price === 'string' ? parseFloat(item.selling_price) || 0 : item.selling_price;
        
        const itemData: any = {
          medicine_name: item.name.trim(),
          batch_no: item.batch_no.trim(),
          quantity: quantity,
          quantity_type: quantityType,
          selling_price: sellingPrice
        };

        // Add tablets_per_strip only for strip type
        if (quantityType === 'strip') {
          itemData.tablets_per_strip = item.tablets_per_strip;
        }

        return itemData;
      });

      // Prepare request body according to new API specification
      const requestBody = {
        customer_name: salesForm.customerName.trim(),
        customer_phone: salesForm.customerPhone.trim(),
        amount_paid: parseFloat(salesForm.amountPaid) || 0,
        payment_mode: salesForm.paymentMode,
        items: items
      };

      console.log('Generating bill with data:', requestBody);

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/sales', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Sales API response received:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to submit sale:', response.status, errorText);
        throw new Error(`Failed to submit sale (Status: ${response.status}): ${errorText}`);
      }

      const result = await response.json();
      console.log('Sale submitted successfully:', result);
      console.log('API Response Message:', result.message);
      
      // Store bill data for confirmation
      setGeneratedBillData({
        ...result,
        customerName: salesForm.customerName,
        customerPhone: salesForm.customerPhone,
        paymentMode: salesForm.paymentMode,
        amountPaid: parseFloat(salesForm.amountPaid),
        billItems: billItems.filter(item => 
          item.name.trim() !== '' && 
          item.quantity !== '' && 
          item.selling_price !== ''
        ),
        subtotal: calculateSubtotal(),
        gst: calculateGST(),
        total: calculateTotal(),
        finalTotal: calculateFinalTotal()
      });
      
      console.log('Generated Bill Data:', generatedBillData);
      
      // Show success notification instead of bill preview
      setShowSuccessNotification(true);
      
      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 3000);
      
      // Reset only sales form, keep bill items for review
      setSalesForm({
        customerName: '',
        customerPhone: '',
        paymentMode: 'cash',
        amountPaid: ''
      });
      // Don't reset bill items immediately - let user review the bill
      // setBillItems will be reset when user clicks "Clear All" or starts new bill

    } catch (error) {
      console.error('Error submitting sale:', error);
      setSaleSubmissionError(error.message);
    } finally {
      setIsSubmittingSale(false);
    }
  };

  const generatePDFBill = () => {
    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to generate the bill');
      return;
    }

    const billDate = new Date().toLocaleDateString('en-IN');
    const billTime = new Date().toLocaleTimeString('en-IN');
    const billNumber = `BILL-${Date.now()}`;
    
    const subtotal = calculateSubtotal();
    const gst = calculateGST();
    const discount = calculateDiscount();
    const total = calculateFinalTotal();

    const billHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pharmacy Bill - ${billNumber}</title>
        <style>
          body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            color: #333;
          }
          .bill-header {
            text-align: center;
            border-bottom: 3px solid #1B5E20;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .pharmacy-name {
            font-size: 28px;
            font-weight: bold;
            color: #1B5E20;
            margin-bottom: 5px;
          }
          .pharmacy-details {
            font-size: 14px;
            color: #666;
            line-height: 1.4;
          }
          .bill-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            flex-wrap: wrap;
          }
          .bill-details, .customer-details {
            flex: 1;
            min-width: 250px;
          }
          .bill-details h3, .customer-details h3 {
            color: #1B5E20;
            font-size: 16px;
            margin-bottom: 10px;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 5px;
          }
          .bill-details p, .customer-details p {
            margin: 5px 0;
            font-size: 14px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          .items-table th {
            background: #1B5E20;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-size: 14px;
            font-weight: bold;
          }
          .items-table td {
            padding: 10px 8px;
            border-bottom: 1px solid #e0e0e0;
            font-size: 13px;
          }
          .items-table tr:nth-child(even) {
            background: #f8f9fa;
          }
          .summary-section {
            margin-top: 20px;
            border-top: 2px solid #1B5E20;
            padding-top: 20px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            margin: 8px 0;
            font-size: 14px;
          }
          .summary-label {
            font-weight: 500;
          }
          .summary-value {
            font-weight: 600;
          }
          .total-row {
            border-top: 2px solid #1B5E20;
            padding-top: 10px;
            margin-top: 15px;
            font-size: 18px;
            font-weight: bold;
            color: #1B5E20;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #e0e0e0;
            padding-top: 20px;
          }
          @media print {
            body { margin: 0; }
            .bill-header { page-break-inside: avoid; }
            .items-table { page-break-inside: avoid; }
            .summary-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="bill-header">
          <div class="pharmacy-name">GREEN VALLEY PHARMACY</div>
          <div class="pharmacy-details">
            123 Health Street, Medical District<br>
            Mumbai, Maharashtra - 400001<br>
            Phone: +91 98765 43210 | Email: info@greenvalleypharmacy.com<br>
            GST No: 27ABCDE1234F1Z5 | License: MH/2023/PH/001
          </div>
        </div>

        <div class="bill-info">
          <div class="bill-details">
            <h3>Bill Information</h3>
            <p><strong>Bill No:</strong> ${billNumber}</p>
            <p><strong>Date:</strong> ${billDate}</p>
            <p><strong>Time:</strong> ${billTime}</p>
            <p><strong>Cashier:</strong> Admin</p>
          </div>
          <div class="customer-details">
            <h3>Customer Information</h3>
            <p><strong>Name:</strong> ${generatedBillData?.customerName || salesForm.customerName || 'N/A'}</p>
            <p><strong>Phone:</strong> ${generatedBillData?.customerPhone || salesForm.customerPhone || 'N/A'}</p>
            <p><strong>Email:</strong> N/A</p>
            <p><strong>Address:</strong> N/A</p>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 5%;">S.No</th>
              <th style="width: 35%;">Medicine Name</th>
              <th style="width: 15%;">Brand</th>
              <th style="width: 10%;">Qty</th>
              <th style="width: 10%;">MRP</th>
              <th style="width: 10%;">GST%</th>
              <th style="width: 15%;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${billItems.map((item, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${item.name}</td>
                <td>${item.medicine_company}</td>
                <td>${item.quantity}</td>
                <td>₹${(typeof item.mrp === 'string' ? parseFloat(item.mrp) || 0 : item.mrp).toFixed(2)}</td>
                <td>${typeof item.gst === 'string' ? parseFloat(item.gst) || 0 : item.gst}%</td>
                <td>₹${((typeof item.mrp === 'string' ? parseFloat(item.mrp) || 0 : item.mrp) * (typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity)).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="summary-section">
          <div class="summary-row">
            <span class="summary-label">Subtotal:</span>
            <span class="summary-value">₹${subtotal.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">GST (${billItems.reduce((sum, item) => sum + (typeof item.gst === 'string' ? parseFloat(item.gst) || 0 : item.gst), 0) / billItems.length}%):</span>
            <span class="summary-value">₹${gst.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Total:</span>
            <span class="summary-value">₹${(subtotal + gst).toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Discount (5%):</span>
            <span class="summary-value">-₹${discount.toFixed(2)}</span>
          </div>
          <div class="summary-row total-row">
            <span class="summary-label">Final Total:</span>
            <span class="summary-value">₹${total.toFixed(2)}</span>
          </div>
        </div>

        <div class="footer">
          <p><strong>Thank you for choosing Green Valley Pharmacy!</strong></p>
          <p>For any queries, please contact us at +91 98765 43210</p>
          <p>This is a computer-generated bill and does not require signature</p>
          <p>Generated on ${billDate} at ${billTime}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(billHTML);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    };

    alert('Bill generated successfully! PDF will open for printing.');
  };

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes bounce {
            0%, 80%, 100% { 
              transform: scale(0);
            } 
            40% { 
              transform: scale(1);
            }
          }
          
          @keyframes slideInRight {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeOut {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
          
          .billing-container::-webkit-scrollbar {
            display: none;
          }
          .billing-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          /* Custom Horizontal Scrollbar Design */
          .table-scrollable-content {
            overflow-x: auto;
            overflow-y: auto;
            scrollbar-width: thin; /* Firefox */
            scrollbar-color: rgba(59, 130, 246, 0.3) transparent; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
            min-width: 1400px; /* Ensure minimum width for proper scrolling */
            scroll-behavior: smooth;
          }
          
          .table-scrollable-content::-webkit-scrollbar {
            height: 8px;
            width: 8px;
          }
          
          .table-scrollable-content::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 4px;
          }
          
          .table-scrollable-content::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            border-radius: 4px;
            transition: all 0.3s ease;
          }
          
          .table-scrollable-content::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
          }

          /* Hide payment dropdown when bill preview is open */
          .bill-preview-modal {
            z-index: 10000 !important;
          }
          
          .bill-preview-modal ~ * [data-payment-dropdown] {
            z-index: 1 !important;
            display: none !important;
          }
          
          /* Force hide payment dropdown when bill preview is active */
          body:has(.bill-preview-modal) [data-payment-dropdown] {
            z-index: 1 !important;
            display: none !important;
          }
          .table-input {
            width: 100%;
            padding: 0.875rem;
            border: none;
            border-radius: 10px;
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(239, 246, 255, 0.3) 100%);
            fontSize: 0.875rem;
            outline: none;
            color: #1e293b;
            fontWeight: 500;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            boxShadow: 0 2px 8px rgba(59, 130, 246, 0.08);
            minHeight: 48px;
            boxSizing: border-box;
            position: relative;
            overflow: hidden;
          }
          
          .table-input::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
            transition: left 0.6s ease;
          }
          
          .table-input:focus {
            background: linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(239, 246, 255, 0.5) 100%);
            boxShadow: 0 0 0 4px rgba(59, 130, 246, 0.15), 0 8px 25px rgba(59, 130, 246, 0.2);
            transform: translateY(-2px) scale(1.02);
          }
          
          .table-input:focus::before {
            left: 100%;
          }
          
          .table-input:hover {
            background: linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(239, 246, 255, 0.4) 100%);
            boxShadow: 0 4px 15px rgba(59, 130, 246, 0.15);
            transform: translateY(-1px);
          }
          
          /* Modern Table Animations */
          @keyframes tableRowSlideIn {
            0% { 
              opacity: 0; 
              transform: translateX(-20px) scale(0.95); 
            }
            100% { 
              opacity: 1; 
              transform: translateX(0) scale(1); 
            }
          }
          
          @keyframes tableRowPulse {
            0%, 100% { 
              transform: scale(1); 
              box-shadow: 0 4px 20px rgba(59, 130, 246, 0.15); 
            }
            50% { 
              transform: scale(1.01); 
              box-shadow: 0 6px 25px rgba(59, 130, 246, 0.2); 
            }
          }
          
          @keyframes shimmerRow {
            0% { 
              background-position: -200px 0; 
            }
            100% { 
              background-position: calc(200px + 100%) 0; 
            }
          }
          
          @keyframes glowEffect {
            0%, 100% { 
              box-shadow: 0 8px 32px rgba(59, 130, 246, 0.15); 
            }
            50% { 
              box-shadow: 0 12px 40px rgba(59, 130, 246, 0.25); 
            }
          }
          
          /* Table Row Animations */
          .table-row-animate {
            animation: tableRowSlideIn 0.6s ease-out;
          }
          
          .table-row-hover {
            animation: tableRowPulse 2s ease-in-out infinite;
          }
          
          .table-container-glow {
            animation: glowEffect 3s ease-in-out infinite;
          }

          /* Header Scrollable Area */
          .table-header-scrollable {
            overflow-x: auto;
            scrollbar-width: thin; /* Firefox */
            scrollbar-color: rgba(255, 255, 255, 0.3) transparent; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
            scroll-behavior: smooth;
          }
          
          .table-header-scrollable::-webkit-scrollbar {
            height: 8px;
            width: 8px;
          }
          
          .table-header-scrollable::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
          }
          
          .table-header-scrollable::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            transition: all 0.3s ease;
          }
          
          .table-header-scrollable::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }
          
          /* Mobile Responsive Styles */
          @media (max-width: 1024px) {
            /* Tablet Styles */
            .main-container {
              margin: 0 1rem !important;
            }
            
            .billing-container {
              margin: 0 !important;
              padding: 1.5rem !important;
              width: 100% !important;
            }
            
            .tab-switcher {
              width: 300px !important;
            }
          }
          
          /* Desktop Table Visibility */
          .desktop-table-header {
            display: block;
          }
          
          .desktop-table-rows {
            display: block;
          }
          
          .mobile-table-rows {
            display: none;
          }
          
          @media (max-width: 768px) {
            /* Mobile Styles */
            .main-container {
              padding: 0 !important;
              margin: 0 !important;
            }
            
            /* Header Section */
            .header-section {
              padding: 0.75rem !important;
              margin-bottom: 0.5rem !important;
              position: sticky !important;
              top: 0 !important;
              z-index: 100 !important;
            }
            
            .header-container {
              flex-direction: column !important;
              gap: 0.75rem !important;
              padding: 0 !important;
              align-items: stretch !important;
            }
            
            .back-button {
              align-self: flex-start !important;
              order: 1 !important;
              width: 44px !important;
              height: 44px !important;
              font-size: 1.3rem !important;
            }
            
            .header-title {
              order: 2 !important;
              position: static !important;
              transform: none !important;
              font-size: 1.1rem !important;
              text-align: center !important;
              margin: 0 !important;
              padding: 0.25rem 0 !important;
            }
            
            .tab-switcher {
              order: 3 !important;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              margin-top: 0.25rem !important;
            }
            
            .tab-button {
              font-size: 0.75rem !important;
              padding: 0.6rem 0.4rem !important;
              flex: 1 !important;
            }
            
            /* Billing Container */
            .billing-container {
              margin: 0 0.5rem 1rem 0.5rem !important;
              padding: 1rem !important;
            }
            
            /* Sales Form */
            .sales-form-grid {
              grid-template-columns: 1fr !important;
              gap: 1rem !important;
            }
            
            /* Table Container */
            .table-container {
              margin: 0 !important;
              width: 100% !important;
              overflow: visible !important;
              background: white !important;
              border-radius: 8px !important;
              border: 1px solid rgba(226, 232, 240, 0.5) !important;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
            }
            
            .table-scrollable-content {
              overflow-x: auto !important;
              min-width: 1200px !important;
              -webkit-overflow-scrolling: touch !important;
            }
            
            /* Mobile Table Visibility */
            .desktop-table-rows {
              display: none !important;
            }
            
            /* Hide Desktop Table Header on Mobile */
            .desktop-table-header {
              display: none !important;
            }
            
            .mobile-table-rows {
              display: block !important;
              width: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            .mobile-table-row {
              width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding: 0.75rem !important;
            }
            
            .table-scrollable-content {
              overflow: visible !important;
              min-width: auto !important;
            }
            
            .table-header {
              grid-template-columns: 50px 150px 120px 100px 80px 80px 80px 80px 80px 80px 100px 80px 100px 50px !important;
              gap: 0.5rem !important;
              padding: 0.75rem !important;
              font-size: 0.7rem !important;
              min-width: 1000px !important;
            }
            
            .table-row {
              grid-template-columns: 50px 150px 120px 100px 80px 80px 80px 80px 80px 80px 100px 80px 100px 50px !important;
              gap: 0.5rem !important;
              padding: 0.75rem !important;
              min-width: 1000px !important;
            }
            
            .table-input {
              padding: 0.5rem !important;
              font-size: 0.75rem !important;
              border-radius: 6px !important;
              min-height: 36px !important;
            }
            
            /* Action Buttons */
            .action-buttons {
              padding: 1rem !important;
              flex-direction: column !important;
              gap: 0.75rem !important;
            }
            
            .action-button {
              width: 100% !important;
              padding: 0.75rem 1rem !important;
              font-size: 0.9rem !important;
            }
            
            /* Bill Summary */
            .bill-summary-container {
              padding: 1rem !important;
            }
            
            .summary-buttons {
              flex-direction: column !important;
              gap: 0.75rem !important;
            }
            
            .summary-button {
              width: 100% !important;
              padding: 1rem !important;
              font-size: 1rem !important;
            }
          }
          
          @media (max-width: 480px) {
            /* Extra Small Mobile Styles */
            .main-container {
              padding: 0 !important;
            }
            
            .header-section {
              padding: 0.5rem !important;
              margin-bottom: 0.25rem !important;
            }
            
            .back-button {
              width: 40px !important;
              height: 40px !important;
              font-size: 1.1rem !important;
            }
            
            .header-title {
              font-size: 1rem !important;
              padding: 0.25rem 0 !important;
            }
            
            .tab-switcher {
              margin-top: 0.25rem !important;
            }
            
            .tab-button {
              font-size: 0.7rem !important;
              padding: 0.5rem 0.3rem !important;
            }
            
            /* Billing Container */
            .billing-container {
              margin: 0 !important;
              padding: 0.75rem !important;
              width: 100% !important;
            }
            
            /* Table Container */
            .table-container {
              margin: 0 !important;
              width: 100% !important;
              background: white !important;
              border-radius: 6px !important;
              border: 1px solid rgba(226, 232, 240, 0.5) !important;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
            }
            
            .table-scrollable-content {
              min-width: 900px !important;
            }
            
            /* Mobile Table Visibility */
            .desktop-table-rows {
              display: none !important;
            }
            
            /* Hide Desktop Table Header on Mobile */
            .desktop-table-header {
              display: none !important;
            }
            
            .mobile-table-rows {
              display: block !important;
              width: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            .mobile-table-row {
              width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding: 0.75rem !important;
            }
            
            .table-scrollable-content {
              overflow: visible !important;
              min-width: auto !important;
            }
            
            .table-header {
              grid-template-columns: 50px 180px 120px 100px 90px 90px 90px 90px 90px 70px 110px 90px 70px !important;
              font-size: 0.65rem !important;
              padding: 0.5rem !important;
              min-width: 990px !important;
              gap: 0.25rem !important;
            }
            
            .table-row {
              grid-template-columns: 50px 180px 120px 100px 90px 90px 90px 90px 90px 70px 110px 90px 70px !important;
              padding: 0.5rem !important;
              min-width: 990px !important;
              gap: 0.25rem !important;
            }
            
            .table-input {
              padding: 0.4rem !important;
              font-size: 0.7rem !important;
              border-radius: 4px !important;
              min-height: 32px !important;
            }
            
            /* Action Buttons */
            .action-buttons {
              padding: 0.75rem !important;
            }
            
            .action-button {
              padding: 0.6rem 0.8rem !important;
              font-size: 0.8rem !important;
            }
            
            /* Bill Summary */
            .bill-summary-container {
              padding: 0.75rem !important;
            }
            
            .summary-button {
              padding: 0.75rem !important;
              font-size: 0.9rem !important;
            }
          }
          
          @media (max-width: 360px) {
            /* Very Small Mobile Styles */
            .header-title {
              font-size: 0.9rem !important;
            }
            
            .tab-button {
              font-size: 0.65rem !important;
              padding: 0.4rem 0.2rem !important;
            }
            
            .billing-container {
              margin: 0 !important;
              padding: 0.5rem !important;
              width: 100% !important;
            }
            
            /* Mobile Table Visibility */
            .desktop-table-rows {
              display: none !important;
            }
            
            /* Hide Desktop Table Header on Mobile */
            .desktop-table-header {
              display: none !important;
            }
            
            .mobile-table-rows {
              display: block !important;
              width: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            .mobile-table-row {
              width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding: 0.75rem !important;
            }
            
            .table-scrollable-content {
              overflow: visible !important;
              min-width: auto !important;
            }
            
            .table-header {
              grid-template-columns: 40px 160px 100px 80px 80px 80px 80px 80px 80px 60px 100px 80px 60px !important;
              font-size: 0.6rem !important;
              padding: 0.4rem !important;
              min-width: 880px !important;
            }
            
            .table-row {
              grid-template-columns: 40px 160px 100px 80px 80px 80px 80px 80px 80px 60px 100px 80px 60px !important;
              padding: 0.4rem !important;
              min-width: 880px !important;
            }
            
            .table-input {
              padding: 0.3rem !important;
              font-size: 0.65rem !important;
              min-height: 28px !important;
            }
          }
          
          /* Modal Responsive Styles */
          @media (max-width: 768px) {
            .bill-preview-modal {
              padding: 1rem !important;
            }
            
            .bill-preview-modal > div {
              padding: 1rem !important;
            }
            
            .bill-confirmation-modal {
              padding: 0.5rem !important;
            }
            
            .bill-confirmation-modal > div {
              padding: 0.5rem !important;
              max-width: 100% !important;
            }
            
            .bill-confirmation-modal .pharmacy-header {
              padding: 1rem 0.5rem !important;
            }
            
            .bill-confirmation-modal .pharmacy-name {
              font-size: 1.5rem !important;
            }
            
            .bill-confirmation-modal .pharmacy-details {
              font-size: 0.8rem !important;
            }
            
            .bill-confirmation-modal .bill-info {
              padding: 0.75rem 0.5rem !important;
            }
            
            .bill-confirmation-modal .customer-info {
              padding: 0.75rem 0.5rem !important;
            }
            
            .bill-confirmation-modal .items-section {
              padding: 0.75rem 0.5rem !important;
            }
            
            .bill-confirmation-modal .summary-section {
              padding: 0.75rem 0.5rem !important;
            }
            
            .bill-confirmation-modal .payment-section {
              padding: 0.75rem 0.5rem !important;
            }
            
            .bill-confirmation-modal .footer-section {
              padding: 1rem 0.5rem !important;
            }
            
            .bill-confirmation-modal .action-buttons {
              flex-direction: column !important;
              gap: 0.75rem !important;
            }
            
            .bill-confirmation-modal .action-button {
              width: 100% !important;
              padding: 0.75rem !important;
            }
            
            /* Mobile-specific improvements */
            .table-input:focus {
              transform: scale(1.02) !important;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2) !important;
            }
            
            .action-button:active {
              transform: scale(0.98) !important;
            }
            
            .tab-button:active {
              transform: scale(0.95) !important;
            }
            
            /* Touch-friendly spacing */
            .table-row {
              min-height: 60px !important;
            }
            
            .table-input {
              min-height: 44px !important;
              touch-action: manipulation !important;
            }
            
            /* Better mobile scrolling */
            .table-scrollable-content {
              -webkit-overflow-scrolling: touch !important;
              scroll-behavior: smooth !important;
            }
            
          }
          
          @media (max-width: 480px) {
            .bill-preview-modal {
              padding: 0.5rem !important;
            }
            
            .bill-preview-modal > div {
              padding: 0.5rem !important;
            }
            
            .bill-confirmation-modal {
              padding: 0.25rem !important;
            }
            
            .bill-confirmation-modal > div {
              padding: 0.25rem !important;
            }
            
            .bill-confirmation-modal .pharmacy-header {
              padding: 0.75rem 0.25rem !important;
            }
            
            .bill-confirmation-modal .pharmacy-name {
              font-size: 1.2rem !important;
            }
            
            .bill-confirmation-modal .pharmacy-details {
              font-size: 0.7rem !important;
            }
            
            .bill-confirmation-modal .bill-info,
            .bill-confirmation-modal .customer-info,
            .bill-confirmation-modal .items-section,
            .bill-confirmation-modal .summary-section,
            .bill-confirmation-modal .payment-section {
              padding: 0.5rem 0.25rem !important;
            }
            
            .bill-confirmation-modal .footer-section {
              padding: 0.75rem 0.25rem !important;
            }
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
          
          /* Medicine search spinner animation */
          .medicine-search-spinner {
            animation: spin 1s linear infinite;
          }
          
          /* Response Notice Animations */
          @keyframes slideInDown {
            0% {
              transform: translateY(-50px);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
            }
            50% {
              transform: scale(1.02);
              box-shadow: 0 8px 20px rgba(245, 158, 11, 0.3);
            }
          }
          
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-5px);
            }
          }
          
          @keyframes shimmer {
            0% {
              background-position: -200px 0;
            }
            100% {
              background-position: calc(200px + 100%) 0;
            }
          }
          
          @keyframes shine {
            0% { 
              background-position: -100% 0;
              opacity: 0;
            }
            20% { 
              opacity: 1;
            }
            80% { 
              opacity: 1;
            }
            100% { 
              background-position: 100% 0;
              opacity: 0;
            }
          }
          
          @keyframes diagonalShimmer {
            0% {
              transform: translateX(-100%) translateY(-100%) rotate(45deg);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(100%) translateY(100%) rotate(45deg);
              opacity: 0;
            }
          }
          
          .sales-form-focused {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 197, 253, 0.2) 50%, rgba(219, 234, 254, 0.15) 100%) !important;
            border-color: rgba(59, 130, 246, 0.4) !important;
            box-shadow: 0 16px 48px rgba(59, 130, 246, 0.25), 0 12px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4) !important;
            position: relative;
            overflow: hidden;
          }
          
          .sales-form-focused::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(90deg, transparent 0%, transparent 20%, rgba(255, 255, 255, 0.8) 50%, transparent 80%, transparent 100%);
            background-size: 200% 100%;
            animation: shine 2s ease-in-out;
            pointer-events: none;
            z-index: 1;
          }
          
          .sales-form-focused > * {
            position: relative;
            z-index: 2;
          }
          
          /* Placeholder styling - grey color */
          .sales-form-focused input::placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          .sales-form-focused input::-webkit-input-placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          .sales-form-focused input::-moz-placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          .sales-form-focused input:-ms-input-placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          /* General placeholder styling for all inputs */
          input::placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          input::-webkit-input-placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          input::-moz-placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          input:-ms-input-placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          
          /* Simple dropdown styling - clean and minimal */
          select {
            width: 100%;
            padding: 0.5rem;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            font-size: 0.875rem;
            background-color: #ffffff;
            cursor: pointer;
            outline: none;
          }
          
          select:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
          }
          
          /* Hidden scrollbar for payment dropdown */
          .payment-dropdown::-webkit-scrollbar {
            display: none;
          }
          
          .payment-dropdown {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          /* Response Notice Animations */
          .response-notice {
            animation: slideInDown 0.6s ease-out, pulse 2s ease-in-out 0.6s infinite;
          }
          
          .response-notice .star-icon {
            animation: float 3s ease-in-out infinite;
          }
          
          .response-notice .check-icon {
            animation: pulse 1.5s ease-in-out infinite;
          }
          
          .response-notice .shimmer-effect {
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
            background-size: 200px 100%;
            animation: shimmer 2s infinite;
          }
          
            /* Mobile Table Layout */
            @media (max-width: 768px) {
              .mobile-table-row {
                display: block !important;
                margin-bottom: 1rem;
                padding: 1rem;
                background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                border: 1px solid rgba(59, 130, 246, 0.1);
                position: relative;
                overflow: hidden;
              }
              
              .mobile-table-row::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: linear-gradient(90deg, #225fe1 0%, #3b82f6 100%);
              }
              
              .mobile-field-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0.75rem 0;
                border-bottom: 1px solid rgba(59, 130, 246, 0.1);
                transition: all 0.2s ease;
              }
              
              .mobile-field-row:hover {
                background: rgba(59, 130, 246, 0.02);
                border-radius: 6px;
                margin: 0 -0.5rem;
                padding: 0.75rem 0.5rem;
              }
              
              .mobile-field-row:last-child {
                border-bottom: none;
              }
              
              .mobile-field-label {
                font-weight: 700;
                color: #1e40af;
                font-size: 0.75rem;
                min-width: 40%;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                flex-shrink: 0;
                display: flex;
                align-items: center;
              }
              
              .mobile-field-label::before {
                content: '';
                width: 4px;
                height: 4px;
                background: #225fe1;
                border-radius: 50%;
                margin-right: 0.5rem;
                flex-shrink: 0;
              }
              
              .mobile-field-value {
                flex: 1;
                margin-left: 1rem;
              }
              
              .mobile-field-value .table-input {
                width: 100%;
                padding: 0.75rem;
                border: 2px solid transparent;
                background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
                border-radius: 8px;
                font-size: 0.85rem;
                min-height: 40px;
                transition: all 0.3s ease;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
              }
              
              .mobile-field-value .table-input:focus {
                background: #ffffff;
                border-color: #225fe1;
                box-shadow: 0 0 0 3px rgba(34, 95, 225, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1);
                transform: translateY(-1px);
              }
              
              .mobile-field-value .table-input:hover {
                background: #ffffff;
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
              }
              
              .mobile-field-value select {
                width: 100%;
                padding: 0.75rem;
                border: 2px solid transparent;
                background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
                border-radius: 8px;
                font-size: 0.85rem;
                min-height: 40px;
                transition: all 0.3s ease;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
              }
              
              .mobile-field-value select:focus {
                background: #ffffff;
                border-color: #225fe1;
                box-shadow: 0 0 0 3px rgba(34, 95, 225, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1);
                transform: translateY(-1px);
              }
              
              .mobile-field-value select:hover {
                background: #ffffff;
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
              }
              
              .mobile-delete-button {
                position: absolute;
                top: 0.75rem;
                right: 1rem;
                background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
                border: 2px solid #dc2626;
                border-radius: 50%;
                width: 36px;
                height: 36px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 18px;
                font-weight: bold;
                color: white;
                transition: all 0.3s ease;
                z-index: 1000;
                box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
              }
              
              .mobile-delete-button:hover {
                background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
                border-color: #b91c1c;
                transform: scale(1.1);
                box-shadow: 0 6px 12px rgba(220, 38, 38, 0.4);
              }
              
              .mobile-delete-button:active {
                transform: scale(0.95);
                box-shadow: 0 2px 4px rgba(220, 38, 38, 0.3);
              }
              
              /* Hide desktop table on mobile */
              .desktop-table {
                display: none !important;
              }
              
              /* Show mobile table on mobile */
              .mobile-table {
                display: block !important;
              }
            }
            
            /* Desktop Table Layout */
            @media (min-width: 769px) {
              .mobile-table {
                display: none !important;
              }
              
              .desktop-table {
                display: block !important;
              }
            }

            /* Bill Preview Responsive Styles */
            @media (max-width: 768px) {
            .bill-preview-modal {
              padding: 0.5rem !important;
            }
            
            .bill-preview-content {
              padding: 1rem !important;
              max-width: 100% !important;
            }
            
            .response-notice {
              padding: 0.75rem !important;
              margin-bottom: 1rem !important;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
            }
            
            .response-notice h3 {
              font-size: 0.9rem !important;
              flex-wrap: wrap !important;
            }
            
            .response-notice p {
              font-size: 0.8rem !important;
              line-height: 1.3 !important;
              word-wrap: break-word !important;
              overflow-wrap: break-word !important;
            }
            
            .bill-info-grid {
              grid-template-columns: 1fr !important;
              gap: 1rem !important;
            }
            
            .items-table {
              font-size: 0.8rem !important;
            }
            
            .items-table th,
            .items-table td {
              padding: 0.5rem !important;
            }
          }
          
          @media (max-width: 480px) {
            .response-notice {
              padding: 0.5rem !important;
              margin-bottom: 0.75rem !important;
            }
            
            .response-notice h3 {
              font-size: 0.8rem !important;
            }
            
            .response-notice p {
              font-size: 0.75rem !important;
              line-height: 1.2 !important;
            }
            
            .response-notice .check-icon {
              width: 35px !important;
              height: 35px !important;
            }
            
            .response-notice .star-icon {
              width: 25px !important;
              height: 25px !important;
              top: -3px !important;
              right: -3px !important;
            }
          }
          
          /* Custom scrollbar for table */
          .table-scroll-container::-webkit-scrollbar {
            height: 12px;
          }
          
          .table-scroll-container::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 6px;
          }
          
          .table-scroll-container::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            border-radius: 6px;
            border: 2px solid #f1f5f9;
          }
          
          .table-scroll-container::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
          }
        `}
      </style>
      <div className="main-container" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        fontFamily: '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100vw'
      }}>
        {/* Header */}
        {showHeader && (
        <div className="header-section" style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderBottom: '1px solid rgba(34, 197, 94, 0.2)',
          padding: '1rem',
          marginBottom: '1rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)'
        }}>
          <div className="header-container" style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '0 1rem',
            position: 'relative'
          }}>
            {/* Back Button - Far Left */}
            <button
              className="back-button"
              onClick={() => navigateTo('pharma-homepage')}
              style={{
                background: 'linear-gradient(135deg, #1B5E20 0%, #0D4A14 100%)',
                color: 'white',
                border: 'none',
                padding: '0.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(27, 94, 32, 0.3)',
                transition: 'all 0.3s ease',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '0',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(27, 94, 32, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(27, 94, 32, 0.3)';
              }}
            >
              ←
            </button>

            {/* Center Heading - Absolutely Centered */}
            <div className="header-title" style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1B5E20',
              letterSpacing: '0.5px',
              textShadow: '0 2px 4px rgba(27, 94, 32, 0.1)',
              whiteSpace: 'nowrap'
            }}>
              CHECKOUT & BILLING
            </div>

            {/* Tab Switcher - Right Side with padding */}
            <div className="tab-switcher" style={{
              position: 'relative',
              display: 'flex',
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '12px',
              padding: '2px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              overflow: 'hidden',
              width: '350px',
              maxWidth: '50vw',
              marginLeft: 'auto',
              marginRight: '2rem',
              flexShrink: 0
            }}>
              {/* Sliding Background */}
              <div
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: getTabPosition(),
                  width: 'calc(33.33% - 2px)',
                  height: 'calc(100% - 4px)',
                  background: isAnimating ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)' : 'linear-gradient(135deg, #225fe1 0%, #1B5E20 100%)',
                  borderRadius: '10px',
                  transition: isAnimating ? 'all 0.7s cubic-bezier(0.25, 1.7, 0.35, 0.8)' : 'all 0.3s ease',
                  boxShadow: isAnimating ? '0 4px 16px rgba(255, 107, 107, 0.5)' : '0 2px 8px rgba(34, 95, 225, 0.3)',
                  zIndex: 1,
                  transform: isAnimating ? 'scale(1.1)' : 'scale(1)',
                  filter: isAnimating ? 'brightness(1.2)' : 'brightness(1)'
                }}
              />
              
              <button
                className="tab-button"
                onClick={() => {
                  console.log('RESTOCK button clicked from checkout-billing');
                  triggerAnimation('restock');
                  navigateTo('medication-addition');
                }}
                style={{
                  position: 'relative',
                  zIndex: 2,
                  padding: '0.5rem 0.75rem',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'transparent',
                  color: getButtonColor('restock'),
                  width: '33.33%',
                  transform: activeTab === 'restock' && isAnimating ? 'scale(1.15)' : 'scale(1)',
                  transition: 'transform 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), color 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), border 0.3s ease',
                  border: isAnimating ? '2px solid #ff6b6b' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'restock') {
                    e.currentTarget.style.color = '#225fe1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'restock') {
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                RESTOCK
              </button>
              
              <button
                className="tab-button"
                onClick={() => {
                  console.log('CHECKOUT button clicked from checkout-billing');
                  triggerAnimation('checkout');
                  navigateTo('checkout-billing');
                }}
                style={{
                  position: 'relative',
                  zIndex: 2,
                  padding: '0.5rem 0.75rem',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'transparent',
                  color: getButtonColor('checkout'),
                  width: '33.33%',
                  transform: activeTab === 'checkout' && isAnimating ? 'scale(1.15)' : 'scale(1)',
                  transition: 'transform 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), color 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), border 0.3s ease',
                  border: isAnimating ? '2px solid #ff6b6b' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'checkout') {
                    e.currentTarget.style.color = '#225fe1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'checkout') {
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                CHECKOUT/BILL
              </button>
              
              <button
                className="tab-button"
                onClick={() => {
                  console.log('INVENTORY button clicked from checkout-billing');
                  triggerAnimation('inventory');
                  navigateTo('inventory');
                }}
                style={{
                  position: 'relative',
                  zIndex: 2,
                  padding: '0.5rem 0.75rem',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'transparent',
                  color: getButtonColor('inventory'),
                  width: '33.33%',
                  transform: activeTab === 'inventory' && isAnimating ? 'scale(1.15)' : 'scale(1)',
                  transition: 'transform 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), color 0.4s cubic-bezier(0.25, 1.7, 0.35, 0.8), border 0.3s ease',
                  border: isAnimating ? '2px solid #ff6b6b' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'inventory') {
                    e.currentTarget.style.color = '#225fe1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'inventory') {
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                INVENTORY
              </button>
            </div>
          </div>
        </div>
        )}

        <div className="billing-container" style={{
          margin: '0 2rem 1rem 2rem',
          padding: '2rem',
          width: 'calc(100% - 4rem)',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
      {/* Bill Items Table */}
          <div style={{
            marginBottom: '2rem',
                    width: '100%',
            maxWidth: '100%',
                    boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#1B5E20',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Bill Items
          </h3>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={addNewItem}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Add Item
            </button>
            
            <button
                  onClick={() => setBillItems(() => ensureEmptyRows([]))}
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Clear All
            </button>
          </div>
        </div>

        {/* TanStack Table */}
        <div 
          className="table-container-glow"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(59, 130, 246, 0.1)',
            overflow: 'visible',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 6px rgba(0, 0, 0, 0.05)',
            marginTop: '1.5rem',
            width: '100%',
            position: 'relative',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          
          <div className="desktop-table">
          <div className="table-scroll-container" style={{ 
            overflowX: 'auto',
            overflowY: 'visible',
            width: '100%',
            maxHeight: '70vh',
            scrollbarWidth: 'thin',
            scrollbarColor: '#3b82f6 #f1f5f9',
            position: 'relative',
            borderRadius: '0 0 16px 16px',
            border: '1px solid #e2e8f0',
            borderTop: 'none'
          }}>
            <table 
                  key={`table-${billItems.length}`}
              style={{ 
                width: 'max-content',
                minWidth: '100%',
                borderCollapse: 'separate',
                borderSpacing: '0',
                padding: '0'
              }}>
              <thead>
                {/* Blue Header Bar with Field Names */}
                <tr style={{ 
                  background: '#225fe1',
                  height: '60px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                  {table.getHeaderGroups()[0]?.headers.map(header => (
                    <td key={header.id} style={{ 
                          padding: '1.25rem 1rem',
                      border: 'none',
                      background: '#225fe1',
                      color: 'white',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                          width: header.getSize(),
                          minWidth: header.getSize(),
                      letterSpacing: '0.025em',
                      textTransform: 'uppercase',
                      borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                    </td>
                    ))}
                  </tr>
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <tr 
                      data-row-id={row.original.id}
                      className="table-row-animate"
                      style={{ 
                        borderBottom: 'none',
                        backgroundColor: 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'visible',
                        animationDelay: `${index * 0.1}s`,
                        marginBottom: '0'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.02)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            padding: '1.25rem 1rem',
                            fontSize: '0.875rem',
                            borderBottom: 'none',
                            borderRight: 'none',
                            verticalAlign: 'middle',
                            minWidth: cell.column.getSize(),
                            width: cell.column.getSize(),
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            borderRadius: '8px',
                            margin: '0.25rem',
                            overflow: 'visible'
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    
                    {/* Full-width Dropdown Row */}
                    {activeDropdown && activeDropdown.rowId === row.original.id && (
                      <tr>
                        <td colSpan={table.getHeaderGroups()[0]?.headers.length || 1} style={{
                          padding: '0',
                          border: 'none',
                          backgroundColor: 'transparent'
                        }}>
                          <div 
                            ref={setDropdownRef}
                            style={{
                              backgroundColor: 'white',
                              border: '3px solid #3b82f6',
                              borderRadius: '12px',
                              boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.4)',
                              margin: '8px',
                              minHeight: '120px',
                              maxHeight: '250px',
                              overflowY: 'auto'
                            }}
                          >
                            {activeDropdown.isLoading ? (
                              <div style={{ 
                                padding: '40px 20px', 
                                textAlign: 'center', 
                                color: '#6b7280',
                                fontSize: '1rem',
                                fontWeight: '500',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                margin: '8px',
                                border: '1px solid #e5e7eb'
                              }}>
                                No medicine
                              </div>
                            ) : activeDropdown.results.length > 0 ? (
                              <div style={{ padding: '8px' }}>
                                {activeDropdown.results.map((medicine, index) => (
                                  <div
                                    key={medicine.id}
                                    data-medicine-index={index}
                                    onClick={() => handleMedicineSelect(activeDropdown.rowId, medicine)}
                                    style={{
                                      padding: '12px',
                                      cursor: 'pointer',
                                      borderBottom: index < activeDropdown.results.length - 1 ? '1px solid #f3f4f6' : 'none',
                                      transition: 'background-color 0.2s ease',
                                      backgroundColor: selectedIndex === index ? '#dbeafe' : 'white',
                                      border: selectedIndex === index ? '2px solid #3b82f6' : 'none',
                                      borderRadius: selectedIndex === index ? '8px' : '0',
                                      margin: selectedIndex === index ? '2px' : '0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (selectedIndex !== index) {
                                        e.currentTarget.style.backgroundColor = '#f8fafc';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (selectedIndex !== index) {
                                        e.currentTarget.style.backgroundColor = 'white';
                                      }
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px', fontSize: '0.9rem' }}>
                                        {medicine.name}
                                      </div>
                                      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '2px' }}>
                                        {medicine.medicine_composition}
                                      </div>
                                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                        {medicine.medicine_company}
                                      </div>
                                    </div>
                                    <div style={{ 
                                      fontSize: '0.8rem', 
                                      color: '#059669', 
                                      fontWeight: '600',
                                      backgroundColor: '#ecfdf5',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      border: '1px solid #d1fae5'
                                    }}>
                                      ₹{medicine.mrp}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ 
                                padding: '40px 20px', 
                                textAlign: 'center', 
                                color: '#6b7280',
                                fontSize: '1rem',
                                fontWeight: '500',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                margin: '8px',
                                border: '1px solid #e5e7eb'
                              }}>
                                No medicine found
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          </div>

          {/* Mobile Table */}
          <div className="mobile-table" style={{ display: 'none' }}>
            {billItems.map((item, index) => (
              <div key={item.id} className="mobile-table-row" style={{ 
                position: 'relative',
                marginBottom: '0',
                transition: 'all 0.3s ease'
              }}>
                {/* Delete Button */}
                <button
                  className="mobile-delete-button"
                  onClick={() => {
                    console.log('🗑️ Mobile delete button clicked for item:', item.id);
                    removeItem(item.id);
                  }}
                  title="Delete this item"
                  style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '1rem',
                    background: '#dc2626',
                    border: '2px solid #dc2626',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'white',
                    zIndex: 1000,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  ×
                </button>
                <div className="mobile-field-row">
                  <div className="mobile-field-label">S.No</div>
                  <div className="mobile-field-value">
                    <div style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                      {index + 1}
                    </div>
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Medicine Name</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleMedicineNameChange(item.id, value);
                      }}
                      placeholder="Enter medicine name"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Composition</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.medicine_composition}
                      onChange={(e) => handleItemFieldChange(item.id, 'medicine_composition', e.target.value)}
                      placeholder="Composition"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Company</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.medicine_company}
                      onChange={(e) => handleItemFieldChange(item.id, 'medicine_company', e.target.value)}
                      placeholder="Company"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Batch No</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.batch_no}
                      onChange={(e) => handleItemFieldChange(item.id, 'batch_no', e.target.value)}
                      placeholder="Batch No"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Quantity Type</div>
                  <div className="mobile-field-value">
                    <select
                      value={item.quantity_type}
                      onChange={(e) => handleItemFieldChange(item.id, 'quantity_type', e.target.value)}
                    >
                      <option value="individual">Individual</option>
                      <option value="strip">Strip</option>
                      <option value="bottle">Bottle</option>
                      <option value="box">Box</option>
                    </select>
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Quantity</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.quantity}
                      onChange={(e) => handleItemFieldChange(item.id, 'quantity', e.target.value)}
                      placeholder="Qty"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Unit Cost</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.unit_cost}
                      onChange={(e) => handleItemFieldChange(item.id, 'unit_cost', e.target.value)}
                      placeholder="Unit Cost"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">MRP</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.mrp}
                      onChange={(e) => handleItemFieldChange(item.id, 'mrp', e.target.value)}
                      placeholder="MRP"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">GST%</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.gst}
                      onChange={(e) => handleItemFieldChange(item.id, 'gst', e.target.value)}
                      placeholder="GST%"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Discount</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.discount}
                      onChange={(e) => handleItemFieldChange(item.id, 'discount', e.target.value)}
                      placeholder="Discount"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Storage</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.storage_location}
                      onChange={(e) => handleItemFieldChange(item.id, 'storage_location', e.target.value)}
                      placeholder="Storage"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Expiry Date</div>
                  <div className="mobile-field-value">
                    <input
                      type="date"
                      value={item.expiry_date}
                      onChange={(e) => handleItemFieldChange(item.id, 'expiry_date', e.target.value)}
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Tablets</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.tablets_per_strip}
                      onChange={(e) => handleItemFieldChange(item.id, 'tablets_per_strip', e.target.value)}
                      placeholder="Tablets"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Selling Price</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.selling_price}
                      onChange={(e) => handleItemFieldChange(item.id, 'selling_price', e.target.value)}
                      placeholder="Selling Price"
                      className="table-input"
                    />
                  </div>
                </div>
                
                <div className="mobile-field-row">
                  <div className="mobile-field-label">Total</div>
                  <div className="mobile-field-value">
                    <input
                      type="text"
                      value={item.total_tablets}
                      onChange={(e) => handleItemFieldChange(item.id, 'total_tablets', e.target.value)}
                      placeholder="Total"
                      className="table-input"
                    />
                  </div>
                </div>
                
                
              </div>
            ))}
          </div>
        </div>
        </div>

          {/* Sales Form */}
          <div className={salesFormFocused ? 'sales-form-focused' : ''} style={{
            marginBottom: '2rem',
            padding: '2rem',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '20px',
            border: '2px solid rgba(59, 130, 246, 0.2)',
            boxShadow: '0 12px 40px rgba(59, 130, 246, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'visible'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '2rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
              position: 'relative'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '16px',
                padding: '0.75rem',
                marginRight: '1rem',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '56px',
                height: '56px',
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {/* Diagonal shimmer effect */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
                  animation: 'diagonalShimmer 3s ease-in-out infinite',
                  pointerEvents: 'none'
                }}></div>
                
                {/* Modern icon with enhanced styling */}
                <div style={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%'
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))' }}>
                    <g fill="none" stroke="white" strokeWidth="1.5">
                      <path strokeLinecap="round" d="M13 3.002Q12.295 3 11.5 3C7.022 3 4.782 3 3.391 4.391S2 8.021 2 12.5c0 4.478 0 6.718 1.391 8.109S7.021 22 11.5 22c4.478 0 6.718 0 8.109-1.391S21 16.979 21 12.5q0-.795-.002-1.5"/>
                      <path strokeLinejoin="round" d="m18.5 2l.258.697c.338.914.507 1.371.84 1.704c.334.334.791.503 1.705.841L22 5.5l-.697.258c-.914.338-1.371.507-1.704.84c-.334.334-.503.791-.841 1.705L18.5 9l-.258-.697c-.338-.914-.507-1.371-.84-1.704c-.334-.334-.791-.503-1.705-.841L15 5.5l.697-.258c.914-.338 1.371-.507 1.704-.84c.334-.334.503-.791.841-1.705z"/>
                      <path strokeLinecap="round" d="M7 17.5c2.332-2.442 6.643-2.557 9 0M13.995 10c0 1.38-1.12 2.5-2.503 2.5A2.5 2.5 0 0 1 8.988 10c0-1.38 1.12-2.5 2.504-2.5a2.5 2.5 0 0 1 2.503 2.5Z"/>
                    </g>
                  </svg>
                </div>
              </div>
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1e40af',
                  margin: '0 0 0.5rem 0',
                  lineHeight: '1.2'
                }}>
                  Sales Information
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#6b7280',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Customer and payment details
                </p>
              </div>
            </div>

            <div className="sales-form-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
                {/* Customer Name */}
                <div style={{
                  background: '#ffffff',
                  borderRadius: '18px',
                  padding: '1.5rem',
                  border: '2px solid rgba(59, 130, 246, 0.2)',
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.12)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'visible'
                }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.75rem'
                  }}>
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter customer name"
                    value={salesForm.customerName}
                    onChange={(e) => handleSalesFormChange('customerName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      background: '#ffffff',
                      fontSize: '0.9rem',
                      outline: 'none',
                      color: '#374151',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      fontWeight: '600'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      setSalesFormFocused(true);
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                      setSalesFormFocused(false);
                    }}
                  />
                </div>

              {/* Customer Phone */}
              <div style={{
                background: '#ffffff',
                borderRadius: '18px',
                padding: '1.5rem',
                border: '2px solid rgba(59, 130, 246, 0.15)',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.1)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'visible'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.75rem'
                }}>
                  Customer Phone *
                </label>
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  value={salesForm.customerPhone}
                  onChange={(e) => handleSalesFormChange('customerPhone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.08) 100%)',
                    fontSize: '0.9rem',
                    outline: 'none',
                    color: '#374151',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                    fontWeight: '600'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Payment Mode */}
              <div style={{
                background: '#ffffff',
                borderRadius: '18px',
                padding: '1.5rem',
                border: '2px solid rgba(59, 130, 246, 0.15)',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.1)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'visible'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.75rem'
                }}>
                  Payment Mode
                </label>
                {/* Custom Dropdown */}
                <div style={{ position: 'relative', width: '100%', zIndex: showBillPreview ? 1 : 10000 }} data-payment-dropdown>
                  <div
                    onClick={() => setIsPaymentDropdownOpen(!isPaymentDropdownOpen)}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      background: '#ffffff',
                      fontSize: '0.9rem',
                      outline: 'none',
                      color: '#374151',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      position: 'relative'
                    }}
                    onFocus={() => setSalesFormFocused(true)}
                    onBlur={() => setSalesFormFocused(false)}
                    tabIndex={0}
                  >
                    <span style={{ 
                      textTransform: 'capitalize',
                      color: '#374151',
                      fontWeight: '600'
                    }}>
                      {salesForm.paymentMode === 'netbanking' ? 'Net Banking' : salesForm.paymentMode.charAt(0).toUpperCase() + salesForm.paymentMode.slice(1)}
                    </span>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 20 20" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        transform: isPaymentDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        stroke: '#374151',
                        strokeWidth: '1.5'
                      }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4"/>
                    </svg>
                  </div>
                  
                  {/* Dropdown Options */}
                  {isPaymentDropdownOpen && (
                    <div className="payment-dropdown" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#ffffff',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1)',
                      zIndex: showBillPreview ? 1 : 99999,
                      marginTop: '4px',
                      overflow: 'hidden',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}>
                      {[
                        { value: 'cash', label: 'Cash' },
                        { value: 'card', label: 'Card' },
                        { value: 'upi', label: 'UPI' },
                        { value: 'netbanking', label: 'Net Banking' },
                        { value: 'wallet', label: 'Digital Wallet' },
                        { value: 'cheque', label: 'Cheque' },
                        { value: 'banktransfer', label: 'Bank Transfer' },
                        { value: 'crypto', label: 'Cryptocurrency' },
                        { value: 'installment', label: 'Installment' },
                        { value: 'credit', label: 'Credit' }
                      ].map((option, index) => (
                        <div
                          key={option.value}
                          onClick={() => {
                            handleSalesFormChange('paymentMode', option.value);
                            setIsPaymentDropdownOpen(false);
                          }}
                          style={{
                            padding: '0.875rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            background: salesForm.paymentMode === option.value 
                              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 197, 253, 0.2) 100%)'
                              : 'transparent',
                            color: salesForm.paymentMode === option.value ? '#1e40af' : '#374151',
                            fontWeight: salesForm.paymentMode === option.value ? '700' : '600',
                            borderBottom: index < 9 ? '1px solid rgba(59, 130, 246, 0.1)' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (salesForm.paymentMode !== option.value) {
                              const target = e.target as HTMLElement;
                              target.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 197, 253, 0.12) 100%)';
                              target.style.color = '#1e40af';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (salesForm.paymentMode !== option.value) {
                              const target = e.target as HTMLElement;
                              target.style.background = 'transparent';
                              target.style.color = '#374151';
                            }
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Amount Paid */}
              <div style={{
                background: '#ffffff',
                borderRadius: '18px',
                padding: '1.5rem',
                border: '2px solid rgba(59, 130, 246, 0.15)',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.1)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'visible'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.75rem'
                }}>
                  Amount Paid *
                </label>
                <input
                  type="number"
                  placeholder="Enter amount paid"
                  value={salesForm.amountPaid}
                  onChange={(e) => handleSalesFormChange('amountPaid', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.08) 100%)',
                    fontSize: '0.9rem',
                    outline: 'none',
                    color: '#374151',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                    fontWeight: '600'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Error Message */}
            {saleSubmissionError && (
            <div style={{
                    padding: '1rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                marginBottom: '1rem',
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                {saleSubmissionError}
              </div>
            )}

          </div>

          

        </div>
      </div>

      {/* Bill Summary - Separate Container */}
      <div className="bill-summary-container" style={{
        margin: '0 2rem 1rem 2rem',
        padding: '2rem',
        width: 'calc(100% - 4rem)',
        maxWidth: '100%',
        boxSizing: 'border-box',
        background: 'rgba(248, 250, 252, 0.8)',
        borderRadius: '8px',
        border: '1px solid rgba(226, 232, 240, 0.5)'
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#1B5E20',
          margin: '0 0 1rem 0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 7H15M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Bill Summary
        </h3>
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          padding: '1.5rem',
          border: '1px solid rgba(226, 232, 240, 0.5)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '0.75rem',
            borderBottom: '2px solid rgba(226, 232, 240, 0.3)'
          }}>
            <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151' }}>Subtotal:</span>
            <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937' }}>₹{calculateSubtotal().toFixed(2)}</span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '0.75rem',
            borderBottom: '2px solid rgba(226, 232, 240, 0.3)'
          }}>
            <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151' }}>GST:</span>
            <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937' }}>₹{calculateGST().toFixed(2)}</span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '0.75rem',
            borderBottom: '2px solid rgba(226, 232, 240, 0.3)'
          }}>
            <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151' }}>Discount:</span>
            <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#dc2626' }}>-₹{calculateDiscount().toFixed(2)}</span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '0.75rem',
            borderBottom: '2px solid rgba(59, 130, 246, 0.3)'
          }}>
            <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e40af' }}>Total Amount:</span>
            <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e40af' }}>₹{calculateFinalTotal().toFixed(2)}</span>
          </div>
        </div>
        
        <div className="summary-buttons" style={{
          display: 'flex',
          gap: '1rem',
          marginTop: '1.5rem',
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => {
              setBillItems(() => ensureEmptyRows([]));
              setSalesForm({
                customerName: '',
                customerPhone: '',
                paymentMode: 'cash',
                amountPaid: ''
              });
            }}
            className="summary-button"
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Clear All
          </button>
          
          <button
            onClick={generateBill}
            className="summary-button"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Generate Bill
          </button>
          
          <button
            onClick={submitSale}
            disabled={isSubmittingSale}
            className="summary-button"
            style={{
              background: isSubmittingSale ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isSubmittingSale ? 'not-allowed' : 'pointer',
              boxShadow: isSubmittingSale ? '0 4px 12px rgba(156, 163, 175, 0.25)' : '0 4px 12px rgba(59, 130, 246, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: isSubmittingSale ? 0.7 : 1
            }}
          >
            {isSubmittingSale ? (
              <>
                <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="32" strokeDashoffset="32">
                    <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32" repeatCount="indefinite"/>
                    <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32" repeatCount="indefinite"/>
                  </circle>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Submit Sale
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Bill Preview Modal */}
      {showBillPreview && (
        <div className="bill-preview-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 9999,
          overflow: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div className="bill-preview-content" style={{
            backgroundColor: 'white',
            width: '100%',
            maxWidth: '1200px',
            minHeight: 'auto',
            padding: '2rem',
            position: 'relative',
            borderRadius: '12px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Close Button */}
            <button
              onClick={closeBillPreview}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                color: '#dc2626',
                transition: 'all 0.2s ease',
                zIndex: 10000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>

            {/* Bill Preview Content */}
            <div style={{
              marginBottom: '2rem'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1B5E20',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Bill Preview
              </h2>
              
              {/* Response Message */}
              {(generatedBillData?.message || generatedBillData?.response_message || generatedBillData?.note) && (
                <div 
                  className="response-notice"
                  style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '2px solid #f59e0b',
                    borderRadius: '12px',
                    padding: '1rem',
                    marginBottom: '1.5rem',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'center',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {/* Shimmer Effect */}
                  <div className="shimmer-effect" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none'
                  }}></div>
                  
                  {/* Decorative elements */}
                  <div className="star-icon" style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    width: '30px',
                    height: '30px',
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#f59e0b"/>
                    </svg>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}>
                    <div className="check-icon" style={{
                      background: 'rgba(245, 158, 11, 0.2)',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    
                    <div style={{ 
                      flex: 1, 
                      textAlign: 'center',
                      minWidth: '200px',
                      maxWidth: '100%'
                    }}>
                      <h3 style={{
                        fontSize: '1rem',
                        fontWeight: '700',
                        color: '#92400e',
                        margin: '0 0 0.5rem 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#92400e"/>
                        </svg>
                        Important Notice
                      </h3>
                      <p style={{
                        fontSize: '0.9rem',
                        color: '#92400e',
                        margin: 0,
                        fontWeight: '600',
                        lineHeight: '1.4',
                        textAlign: 'center',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto'
                      }}>
                        {generatedBillData?.message || generatedBillData?.response_message || generatedBillData?.note}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Bill Content */}
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '2rem',
                backgroundColor: '#fafbfc',
                width: '100%',
                overflow: 'hidden'
              }}>
                {/* Pharmacy Header */}
                <div style={{
                  textAlign: 'center',
                  borderBottom: '2px solid #1B5E20',
                  paddingBottom: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#1B5E20',
                    margin: '0 0 0.5rem 0'
                  }}>
                    GREEN VALLEY PHARMACY
                  </h3>
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#666',
                    margin: 0
                  }}>
                    123 Health Street, Medical District, Mumbai, Maharashtra - 400001<br />
                    Phone: +91 98765 43210 | Email: info@greenvalleypharmacy.com
                  </p>
                </div>

                {/* Bill Info */}
                <div className="bill-info-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '2rem',
                  marginBottom: '1.5rem'
                }}>
                  <div>
                    <h4 style={{ color: '#1B5E20', marginBottom: '0.5rem' }}>Bill Information</h4>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                      <strong>Bill No:</strong> BILL-{Date.now()}
                    </p>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                      <strong>Date:</strong> {new Date().toLocaleDateString('en-IN')}
                    </p>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                      <strong>Time:</strong> {new Date().toLocaleTimeString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <h4 style={{ color: '#1B5E20', marginBottom: '0.5rem' }}>Customer Information</h4>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                      <strong>Name:</strong> {generatedBillData?.customerName || salesForm.customerName || 'N/A'}
                    </p>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                      <strong>Phone:</strong> {generatedBillData?.customerPhone || salesForm.customerPhone || 'N/A'}
                    </p>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                      <strong>Payment:</strong> {generatedBillData?.paymentMode?.toUpperCase() || salesForm.paymentMode?.toUpperCase() || 'CASH'}
                    </p>
                  </div>
                </div>

                {/* Items Table */}
                <div style={{ marginBottom: '1.5rem', overflowX: 'auto' }}>
                  <table className="items-table" style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem',
                    minWidth: '800px'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#1B5E20', color: 'white' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #1B5E20', width: '5%' }}>S.No</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #1B5E20', width: '35%' }}>Medicine Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #1B5E20', width: '15%' }}>Brand</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #1B5E20', width: '10%' }}>Qty</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #1B5E20', width: '10%' }}>Price</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #1B5E20', width: '10%' }}>GST%</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #1B5E20', width: '15%' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(generatedBillData?.billItems || billItems.filter(item => 
                        item.name.trim() !== '' && 
                        item.quantity !== '' && 
                        item.selling_price !== ''
                      )).map((item, index) => {
                        const sellingPrice = typeof item.selling_price === 'string' ? parseFloat(item.selling_price) || 0 : item.selling_price;
                        const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity;
                        const gstRate = typeof item.gst === 'string' ? parseFloat(item.gst) || 0 : item.gst;
                        const total = sellingPrice * quantity;
                        
                        return (
                          <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                            <td style={{ padding: '0.75rem', border: '1px solid #e2e8f0' }}>{index + 1}</td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e2e8f0' }}>{item.name}</td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e2e8f0' }}>{item.medicine_company}</td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e2e8f0' }}>{quantity}</td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e2e8f0' }}>₹{sellingPrice.toFixed(2)}</td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e2e8f0' }}>{gstRate}%</td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e2e8f0' }}>₹{total.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Bill Summary */}
                <div style={{
                  borderTop: '2px solid #1B5E20',
                  paddingTop: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <span>Subtotal:</span>
                    <span>₹{(generatedBillData?.subtotal || calculateSubtotal()).toFixed(2)}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <span>GST ({billItems.reduce((sum, item) => sum + (typeof item.gst === 'string' ? parseFloat(item.gst) || 0 : item.gst), 0) / billItems.length}%):</span>
                    <span>₹{(generatedBillData?.gst || calculateGST()).toFixed(2)}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <span>Total:</span>
                    <span>₹{((generatedBillData?.subtotal || calculateSubtotal()) + (generatedBillData?.gst || calculateGST())).toFixed(2)}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <span>Discount:</span>
                    <span style={{ color: '#dc2626' }}>-₹{(generatedBillData?.discount || calculateDiscount()).toFixed(2)}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    color: '#1B5E20',
                    borderTop: '2px solid #1B5E20',
                    paddingTop: '0.5rem',
                    marginTop: '0.5rem'
                  }}>
                    <span>Final Total:</span>
                    <span>₹{(generatedBillData?.finalTotal || calculateFinalTotal()).toFixed(2)}</span>
                  </div>
                </div>

                {/* Footer */}
                <div style={{
                  marginTop: '2rem',
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  color: '#666',
                  borderTop: '1px solid #e0e0e0',
                  paddingTop: '1rem'
                }}>
                  <p style={{ margin: '0.5rem 0', fontWeight: 'bold' }}>
                    <strong>Thank you for choosing Green Valley Pharmacy!</strong>
                  </p>
                  <p style={{ margin: '0.5rem 0' }}>
                    For any queries, please contact us at +91 98765 43210
                  </p>
                  <p style={{ margin: '0.5rem 0' }}>
                    This is a computer-generated bill and does not require signature
                  </p>
                  <p style={{ margin: '0.5rem 0' }}>
                    Generated on {new Date().toLocaleDateString('en-IN')} at {new Date().toLocaleTimeString('en-IN')}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => {
                  generatePDFBill();
                  closeBillPreview();
                }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9H4C3.44772 9 3 9.44772 3 10V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V10C21 9.44772 20.5523 9 20 9H18M6 9V7C6 5.34315 7.34315 4 9 4H15C16.6569 4 18 5.34315 18 7V9M6 9H18M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Print Bill
              </button>
              
              <button
                onClick={() => {
                  downloadBill();
                  closeBillPreview();
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M17 9L12 14L7 9M12 14V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download PDF
              </button>
              
              <button
                onClick={closeBillPreview}
                style={{
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(107, 114, 128, 0.25)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontSize: '1rem',
            fontWeight: '600',
            animation: 'slideInRight 0.5s ease-out, fadeOut 0.5s ease-in 2.5s forwards',
            maxWidth: '400px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Success Icon */}
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4"/>
              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
            </svg>
          </div>
          
          {/* Message */}
          <div>
            Successfully submitted!
          </div>
          
          {/* Close Button */}
          <button
            onClick={() => setShowSuccessNotification(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '12px',
              marginLeft: 'auto',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            ✕
          </button>
        </div>
      )}


    </div>
  );
};

export default CheckoutBilling;
